import { Tag, Tooltip } from 'antd';
import { useChunksMeta, type ChunkMeta } from '@entities/chat';

interface Props {
  chunkIds: string[];
}

/**
 * Плашки источников под ответом ассистента.
 *
 * Группируем чанки по source_id — показываем 1 плашку на источник
 * с агрегированными страницами в tooltip. Это компактнее чем по плашке
 * на каждый chunk_id, и даёт юзеру понятную картину «откуда взято».
 *
 * Graceful degradation: если бэк не отдаёт /knowledge/chunks (mini-PR
 * ещё не применён), useChunksMeta возвращает [] и компонент рисует
 * только сводный счётчик «N фрагментов».
 */
export function CitationPills({ chunkIds }: Props) {
  const { data: chunks } = useChunksMeta(chunkIds);

  if (chunkIds.length === 0) return null;

  // Если метаданные не приехали — показываем просто счётчик.
  if (!chunks || chunks.length === 0) {
    return (
      <div style={{ marginTop: 8 }}>
        <Tag bordered={false} style={{ fontSize: 11 }}>
          {chunkIds.length} {pluralFragments(chunkIds.length)}
        </Tag>
      </div>
    );
  }

  // Группируем по source_id
  const bySource = groupBySource(chunks);

  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {bySource.map(({ sourceId, sourceName, pages }) => {
        const label = sourceName ?? 'Документ';
        const pagesText = pages.length > 0 ? `стр. ${pages.join(', ')}` : null;
        const tooltipText = pagesText ? `${label} · ${pagesText}` : label;

        return (
          <Tooltip key={sourceId} title={tooltipText}>
            <Tag bordered={false} style={{ fontSize: 11, maxWidth: 240 }}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {label}
              </span>
              {pagesText && (
                <span style={{ marginLeft: 6, opacity: 0.6 }}>{pagesText}</span>
              )}
            </Tag>
          </Tooltip>
        );
      })}
    </div>
  );
}

interface SourceGroup {
  sourceId: string;
  sourceName: string | null;
  pages: number[];
}

function groupBySource(chunks: ChunkMeta[]): SourceGroup[] {
  const map = new Map<string, SourceGroup>();
  for (const c of chunks) {
    const existing = map.get(c.source_id);
    if (existing) {
      if (c.page_number != null && !existing.pages.includes(c.page_number)) {
        existing.pages.push(c.page_number);
      }
    } else {
      map.set(c.source_id, {
        sourceId: c.source_id,
        sourceName: c.source_name,
        pages: c.page_number != null ? [c.page_number] : [],
      });
    }
  }
  // Сортируем страницы внутри каждого источника
  for (const g of map.values()) {
    g.pages.sort((a, b) => a - b);
  }
  return Array.from(map.values());
}

function pluralFragments(n: number): string {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'фрагментов';
  if (last === 1) return 'фрагмент';
  if (last >= 2 && last <= 4) return 'фрагмента';
  return 'фрагментов';
}
